import type { WorkflowDefinition } from "@/stores/workflowsStore";
import type { TemplateProp } from "@/stores/templatesStore";
import type { AIWorkflowDefinition } from "@/types/aiChat";
import {
  GOOGLE_SHEETS_NODE_PREFIX,
  GOOGLE_SHEET_TEMPLATE_URL_PROP_KEY,
  MESSAGE_FIELD_MAP,
  NOTIFICATION_ACTION_IDS,
  NOTIFICATION_CHANNELS,
  NOTIFICATION_NODE_TYPE,
  NOTIFICATION_PROP_KEY,
  NOTIFY_NODE_BASE,
  type NotificationConfig,
} from "./notificationChannels";
import { normalizeToMarkdown, slackBoldToMarkdown } from "./normalizeMessage";

/**
 * Recursively applies `fn` to every string leaf in a payload-shaped value.
 * Returns the input reference unchanged when no leaf produced a different
 * string — so visitor-style callers pay no allocation cost, and transformer
 * callers avoid unnecessary copies on no-op branches.
 */
function mapStringLeaves(value: unknown, fn: (s: string) => string): unknown {
  if (typeof value === "string") return fn(value);
  if (Array.isArray(value)) {
    let changed = false;
    const mapped = value.map(v => {
      const n = mapStringLeaves(v, fn);
      if (n !== v) changed = true;
      return n;
    });
    return changed ? mapped : value;
  }
  if (value && typeof value === "object") {
    let changed = false;
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      const n = mapStringLeaves(v, fn);
      if (n !== v) changed = true;
      out[k] = n;
    }
    return changed ? out : value;
  }
  return value;
}

function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

type AnyDefinition = WorkflowDefinition | AIWorkflowDefinition;

export interface DetectedNotification {
  nodeId: string;
  currentActionId: string;
  message: string;
  nodeLabel?: string;
}

export function detectNotificationNodes(definition: AnyDefinition): DetectedNotification[] {
  if (!definition.nodes) return [];
  return Object.entries(definition.nodes)
    .filter(([, node]) => NOTIFICATION_ACTION_IDS.has(node.type))
    .map(([nodeId, node]) => ({
      nodeId,
      currentActionId: node.type,
      message: extractMessageField(node.type, node.payload),
      nodeLabel: node.titleOverride || node.description,
    }));
}

export function extractMessageField(actionId: string, payload: Record<string, unknown>): string {
  const field = MESSAGE_FIELD_MAP[actionId];
  if (!field || !payload) return "";
  const value = payload[field];
  if (typeof value === "string") return value;
  for (const fallback of ["text", "message", "body", "html"]) {
    const v = payload[fallback];
    if (typeof v === "string") return v;
  }
  return "";
}

export function convertToNotificationNodes(definition: AnyDefinition, nodeIdsToConvert: Set<string>): AnyDefinition {
  if (!definition.nodes || nodeIdsToConvert.size === 0) return definition;

  // Channel-specific IDs (e.g. "send_telegram") become neutral "notify" / "notify_N",
  // with collision avoidance against existing non-converted IDs.
  const protectedIds = new Set(Object.keys(definition.nodes).filter(id => !nodeIdsToConvert.has(id)));
  const toConvert = Array.from(nodeIdsToConvert);
  const idRename = new Map<string, string>();
  const usedNewIds = new Set<string>();
  toConvert.forEach((oldId, index) => {
    const base = toConvert.length === 1 ? NOTIFY_NODE_BASE : `${NOTIFY_NODE_BASE}_${index + 1}`;
    let candidate = base;
    let suffix = 1;
    while (protectedIds.has(candidate) || usedNewIds.has(candidate)) {
      candidate = `${base}_${++suffix}`;
    }
    idRename.set(oldId, candidate);
    usedNewIds.add(candidate);
  });

  const rewriteRefs = (ids?: string[]): string[] | undefined => (ids ? ids.map(id => idRename.get(id) ?? id) : ids);

  // Rewrites `{{oldId.section.field}}` in downstream payloads so expression
  // resolution still targets the renamed node. Notification nodes are usually
  // leaves, but edit-mode Telegram flows and fan-out designs can reference them.
  const renamePatterns = Array.from(idRename)
    .filter(([oldId, newId]) => oldId !== newId)
    .map(([oldId, newId]) => ({
      regex: new RegExp(`\\{\\{(\\s*)${escapeRegex(oldId)}\\.`, "g"),
      replacement: `{{$1${newId}.`,
    }));
  const rewriteIdInString = (s: string): string => {
    let out = s;
    for (const { regex, replacement } of renamePatterns) {
      out = out.replace(regex, replacement);
    }
    return out;
  };

  const newNodes: Record<string, (typeof definition.nodes)[string]> = {};
  for (const [nodeId, node] of Object.entries(definition.nodes)) {
    const nextId = idRename.get(nodeId) ?? nodeId;
    const nextChildren = rewriteRefs(node.children);
    const nextLoopBody = rewriteRefs((node as { loopBody?: string[] }).loopBody);

    if (nodeIdsToConvert.has(nodeId)) {
      // Slack uses single-asterisk for bold while Markdown reads it as italic —
      // a source-aware pre-pass disambiguates before the generic normalizer.
      const raw = extractMessageField(node.type, node.payload);
      const preNormalized = node.type.startsWith("slack-") ? slackBoldToMarkdown(raw) : raw;
      const message = normalizeToMarkdown(preNormalized);
      newNodes[nextId] = {
        ...node,
        type: NOTIFICATION_NODE_TYPE,
        payload: { message },
        connector: undefined,
        ...(nextChildren !== undefined ? { children: nextChildren } : {}),
        ...(nextLoopBody !== undefined ? { loopBody: nextLoopBody } : {}),
      };
    } else {
      const nextPayload = node.payload
        ? (mapStringLeaves(node.payload, rewriteIdInString) as typeof node.payload)
        : node.payload;
      newNodes[nextId] = {
        ...node,
        payload: nextPayload,
        ...(nextChildren !== undefined ? { children: nextChildren } : {}),
        ...(nextLoopBody !== undefined ? { loopBody: nextLoopBody } : {}),
      };
    }
  }
  return { ...definition, nodes: newNodes } as AnyDefinition;
}

/**
 * Removes template props that no longer have any `{{$props.X}}` reference
 * in the definition. Used after publish-time notification conversion to
 * drop channel-specific props (e.g. slackChannel) that referenced only
 * the payload fields that were replaced by the virtual notification node.
 *
 * Always keeps `$notification` if any notification node exists.
 */
export function pruneUnusedTemplateProps(definition: AnyDefinition, templateProps: TemplateProp[]): TemplateProp[] {
  if (!templateProps.length || !definition.nodes) return templateProps;

  // Keys are stored without a leading "$" (e.g. `walletAddress`), so capture
  // the identifier after `$props.`. Permit `$` to start the key so future
  // widget-driven props using `{{$props.$foo}}` aren't silently dropped.
  const referenced = new Set<string>();
  for (const node of Object.values(definition.nodes)) {
    if (!node.payload) continue;
    mapStringLeaves(node.payload, s => {
      for (const m of s.matchAll(/\{\{\s*\$props\.([A-Za-z_$][\w$]*)/g)) referenced.add(m[1]);
      return s;
    });
  }

  let hasNotification = false;
  let hasGoogleSheets = false;
  for (const n of Object.values(definition.nodes)) {
    if (n.type === NOTIFICATION_NODE_TYPE) hasNotification = true;
    else if (n.type.startsWith(GOOGLE_SHEETS_NODE_PREFIX)) hasGoogleSheets = true;
    if (hasNotification && hasGoogleSheets) break;
  }

  return templateProps.filter(p => {
    if (p.key === NOTIFICATION_PROP_KEY) return hasNotification;
    if (p.key === GOOGLE_SHEET_TEMPLATE_URL_PROP_KEY) return hasGoogleSheets;
    return referenced.has(p.key);
  });
}

export function createNotificationProp(): TemplateProp {
  return {
    name: "Notification Channel",
    key: NOTIFICATION_PROP_KEY,
    type: "string",
    description: "Choose where to receive notifications",
    required: true,
    properties: {
      inputType: "notification-channel",
    },
  };
}

export function hasNotificationNodes(definition: WorkflowDefinition): boolean {
  if (!definition.nodes) return false;
  return Object.values(definition.nodes).some(node => node.type === NOTIFICATION_NODE_TYPE);
}

export function resolveNotificationNodes(
  definition: WorkflowDefinition,
  notificationConfig: NotificationConfig,
): WorkflowDefinition {
  if (!definition.nodes) return definition;
  const channelDef = NOTIFICATION_CHANNELS[notificationConfig.channel];
  if (!channelDef) {
    throw new Error(`Unknown notification channel: ${notificationConfig.channel}`);
  }
  const newNodes = { ...definition.nodes };
  for (const [nodeId, node] of Object.entries(newNodes)) {
    if (node.type !== NOTIFICATION_NODE_TYPE) continue;
    const message = typeof node.payload?.message === "string" ? node.payload.message : "";
    newNodes[nodeId] = {
      ...node,
      type: channelDef.actionId,
      payload: channelDef.mapPayload(message, notificationConfig.destination, notificationConfig.extras),
      connector: channelDef.connectorType
        ? { type: channelDef.connectorType, id: notificationConfig.connectorId ?? undefined }
        : undefined,
    };
  }
  return { ...definition, nodes: newNodes };
}
